import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import { Environment } from 'dashboard/models/environment';
import { Graph } from 'dashboard/models/graph';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplateFilterOption } from 'dashboard/models/inquiry-template';
import { ProductFeatureName } from 'dashboard/models/organization';
import { TemplateFilterOption } from 'dashboard/models/template';
import { User } from 'dashboard/models/user';
import AccountInquiriesTable from 'dashboard/views/DashboardAccountsShowView/components/AccountInquiriesTable';
import AccountProfile from 'dashboard/views/DashboardAccountsShowView/components/AccountProfile';
import GraphAccountCard from 'dashboard/views/DashboardAccountsShowView/components/GraphAccountCard';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useProductFeature from 'lib/hooks/useProductFeature';
import { PaginationParams } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Permission } from 'lib/permissions';

interface AccountOverviewSectionProps {
  account: Account;
  accountStatuses: AccountStatus[];
  accountTypes: AccountType[];
  currentUser: User;
  currentEnvironment: Environment;
  inquiries?: Inquiry[];
  inquiryTemplateFilterOptions?: (InquiryTemplateFilterOption | TemplateFilterOption)[];
  isLoadingInquiries: boolean;
  graph: Graph;
  graphQueryTemplateId: string;
  graphQueryTemplates: GraphQueryTemplate[];
  featureFlags: FeatureFlagsMap;
  inquiryPaginationParams: PaginationParams;
  inquiryPageSize: number;
  handleInquiryPageLinkClick: (pageNumber: number) => void;
  handleInquiryPageSizeClick: (pageSize: number) => void;
}

const AccountOverviewSection = ({
  account,
  accountStatuses,
  accountTypes,
  currentEnvironment,
  currentUser,
  inquiries,
  isLoadingInquiries,
  inquiryTemplateFilterOptions,
  graph,
  graphQueryTemplateId,
  graphQueryTemplates,
  inquiryPaginationParams,
  inquiryPageSize,
  handleInquiryPageLinkClick,
  handleInquiryPageSizeClick,
}: AccountOverviewSectionProps) => {
  const isGraphEnabled = useProductFeature(ProductFeatureName.Graph);
  const hasGraphViewPermission = usePermission(Permission.GraphView);
  const hasGraphQueryTemplateViewPermission = usePermission(Permission.GraphQueryTemplateView);
  const hasGraphQueryWritePermission = usePermission(Permission.GraphQueryWrite);
  const hasGraphQueryViewPermission = usePermission(Permission.GraphQueryView);
  const graphMaintenanceModeEnabled = useFeatureFlag('dashboard:graph-maintenance-mode');

  return (
    <>
      <AccountProfile
        account={account}
        dataTest='account-profile'
        environmentType={currentEnvironment.type}
      />
      <AccountInquiriesTable
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        inquiries={inquiries}
        loading={isLoadingInquiries}
        inquiryTemplateFilterOptions={inquiryTemplateFilterOptions}
        dataTest='account-inquiries-table'
        paginationParams={inquiryPaginationParams}
        pageSize={inquiryPageSize}
        handlePageLinkClick={handleInquiryPageLinkClick}
        handlePageSizeClick={handleInquiryPageSizeClick}
      />
      {!graphMaintenanceModeEnabled &&
        (isGraphEnabled &&
        hasGraphViewPermission &&
        hasGraphQueryViewPermission &&
        hasGraphQueryWritePermission &&
        hasGraphQueryTemplateViewPermission ? (
          <GraphAccountCard
            account={account}
            accountTypes={accountTypes}
            accountStatuses={accountStatuses}
            currentUser={currentUser}
            currentEnvironment={currentEnvironment}
            graphQueryTemplateId={graphQueryTemplateId}
            graphQueryTemplates={graphQueryTemplates}
            graph={graph}
          />
        ) : null)}
    </>
  );
};

export default AccountOverviewSection;
